import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Eye } from "lucide-react";
import { adminClient } from "./api";
import { ClaimRow } from "./ClaimsBlock";
import RecordDrawer from "./RecordDrawer";

const TABS = [["open", "Open"], ["reviewing", "Reviewing"], ["approved", "Approved"], ["denied", "Denied"], ["resolved", "Resolved"], ["", "All"]];

export default function ClaimsAdmin() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("open");
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await adminClient.get(`/admin/claims${tab ? `?status=${tab}` : ""}`); setData(data); }
    catch { toast.error("Could not load claims"); } finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const counts = data?.counts || {};

  return (
    <div data-testid="claims-admin" className="space-y-5 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-black text-3xl text-[#0C2E2B] flex items-center gap-2"><AlertTriangle className="w-7 h-7" /> Damage claims</h1>
          <p className="text-sm text-zinc-500">You promise a reply within 2 business days — anything past {data?.response_hours || 48}h turns red.</p>
        </div>
        <div className="flex items-center gap-2">
          {data?.overdue > 0 && <span data-testid="claims-overdue-badge" className="bg-red-600 text-white text-xs font-black px-2.5 py-1 rounded-full">{data.overdue} overdue</span>}
          <Button data-testid="claims-refresh-btn" size="icon" variant="ghost" className="h-10 w-10" onClick={load}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="claims-tabs">
        {TABS.map(([k, l]) => (
          <button key={k || "all"} data-testid={`claims-tab-${k || "all"}`} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${tab === k ? "bg-[#0C2E2B] text-white border-[#0C2E2B]" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
            {l}{k && counts[k] ? ` · ${counts[k]}` : ""}
          </button>
        ))}
      </div>

      {!data ? <p className="text-sm text-zinc-500">Loading…</p> : items.length === 0 ? (
        <Card className="p-10 text-center text-zinc-500 text-sm" data-testid="claims-empty">No {tab || ""} claims. Nice.</Card>
      ) : (
        <div className="space-y-3" data-testid="claims-list">
          {items.map((c) => (
            <div key={c.id} className="space-y-1.5">
              <ClaimRow claim={c} onChanged={load} showCustomer />
              <button data-testid={`claim-open-booking-${c.id}`} onClick={() => setOpenId(c.booking_id)} className="text-[11px] font-bold text-[#0C2E2B] underline inline-flex items-center gap-1 ml-1"><Eye className="w-3 h-3" /> Open booking {c.ref_id}</button>
            </div>
          ))}
        </div>
      )}
      <RecordDrawer kind="booking" recordId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
